/*
 * eslint-disable -- o AppLink ja encapsula WLink e WLinkButton, e o divider
 * do painel e uma regra decorativa de 1px, nao texto auxiliar.
 */
/* eslint-disable @alice-health/w-link, @alice-health/w-helper */

import { useEffect, useState } from 'react'
import Image from 'next/image'
import { WParagraph } from '@alice-health/wonderland-react'
import classNames from 'classnames'
import { Link } from '@/global/components/Link'
import { NavbarSubmenu } from './Navbar.types'
import { sendButtonClickedAnalyticEvent } from './Navbar.helpers'

const PREVIEW_SIZE = 104

interface NavbarMenuPanelProps {
  id: string
  title: string
  submenu: NavbarSubmenu
  isOpen: boolean
}

const NavbarMenuPanel = ({
  id,
  title,
  submenu: { links, caption, cta },
  isOpen
}: NavbarMenuPanelProps) => {
  /*
   * Titulo do link em hover, nao a imagem: producao troca o preview no
   * mouseenter do link e o esconde no mouseleave, sem imagem default.
   */
  const [previewTitle, setPreviewTitle] = useState<string | null>(null)

  /*
   * O corpo do painel so monta na primeira abertura. Ele traz 8 AppLink e uma
   * dezena de custom elements do design system, que o Stencil registra e
   * atualiza na thread principal. E o `invisible` do estado fechado mantem caixa
   * de layout, entao o lazy do next/image considera as duas imagens de preview
   * visiveis e as baixa no primeiro paint das 4 rotas. O wrapper posicionado
   * segue montado, para a transicao de opacidade ter alvo.
   */
  const [hasOpened, setHasOpened] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true)
      return
    }

    /*
     * O preview limpa no fechamento, e nao apenas no `onMouseLeave` do corpo:
     * fechar por Escape ou por clique fora nao passa o ponteiro por cima, e a
     * imagem do ultimo link em hover reapareceria na abertura seguinte.
     */
    setPreviewTitle(null)
  }, [isOpen])

  const previews = links.flatMap(link =>
    link.preview ? [{ title: link.title, ...link.preview }] : []
  )

  return (
    <div
      id={id}
      data-testid={`navbar-menu-panel-${title}`}
      aria-hidden={!isOpen}
      className={classNames(
        /* `z-[1]`: atras dos itens do menu, que sao seus irmaos dentro do `nav`. */
        'absolute inset-x-0 top-[var(--navbar-panel-top)] z-[1]',
        'flex min-h-[var(--navbar-panel-height)] flex-col justify-between',
        'rounded-gl-border-radius-xg bg-gl-color-transparent-white-70 pt-[88px]',
        'backdrop-blur-[44px] duration-200',
        /*
         * `invisible` junto do aria-hidden: sem isso os links do painel fechado
         * continuam na ordem de tabulacao, dentro de uma subarvore escondida da
         * arvore de acessibilidade.
         *
         * A propriedade em transicao muda com o estado de destino de proposito.
         * Ao abrir, so o opacity transiciona, entao a visibilidade vira visible
         * na hora e a entrada aparece inteira. Ao fechar, visibility entra na
         * transicao e o navegador a mantem visible ate o meio dos 200ms, o que
         * deixa a saida terminar de sumir antes de o painel sair do fluxo.
         */
        isOpen
          ? 'opacity-100 transition-opacity'
          : 'pointer-events-none invisible opacity-0 transition-[opacity,visibility]'
      )}
    >
      {hasOpened ? (
        <div
          className='relative flex flex-col gap-gl-spacing-04 px-gl-spacing-10 py-gl-spacing-06'
          onMouseLeave={() => setPreviewTitle(null)}
        >
          {links.map(link => (
            <div
              key={link.title}
              className='navbar-panel-link-type w-fit'
              onMouseEnter={() => setPreviewTitle(link.title)}
            >
              <Link
                as='link'
                quiet
                variant='primary'
                size='medium'
                href={link.href}
                onClick={() => sendButtonClickedAnalyticEvent(link.title)}
              >
                {link.title}
              </Link>
            </div>
          ))}

          {previews.length > 0 ? (
            <div
              data-testid='navbar-menu-preview'
              className='absolute right-gl-spacing-06 top-gl-spacing-06 overflow-hidden rounded-gl-border-radius-lg'
              style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
            >
              {previews.map(preview => (
                <Image
                  key={preview.title}
                  src={preview.src}
                  alt={preview.alt}
                  width={PREVIEW_SIZE}
                  height={PREVIEW_SIZE}
                  className={classNames(
                    'absolute inset-0 h-full w-full object-cover transition-opacity duration-200',
                    previewTitle === preview.title ? 'opacity-100' : 'opacity-0'
                  )}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {hasOpened ? (
        <div className='flex flex-col gap-gl-spacing-06 px-gl-spacing-10 pb-gl-spacing-06'>
          <span className='h-px w-full bg-gl-color-transparent-black-10' />

          <div className='flex items-center justify-between gap-gl-spacing-10'>
            <WParagraph
              mode='active'
              size='large'
            >
              {caption}
            </WParagraph>

            {cta ? (
              <Link
                as='button'
                size='medium'
                variant='cta'
                href={cta.href}
                className='shrink-0'
                onClick={() => sendButtonClickedAnalyticEvent(cta.title)}
              >
                {cta.title}
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export { NavbarMenuPanel }
